/**
 * Which Annual Planner empty state a coach's Programming Views select, if any.
 *
 * Rule 2: Tracks View and Blocks View suppress data, so losing either one takes
 * the planner itself with it — the message names the half that can no longer be
 * displayed. Both granted renders the planner normally (`null`).
 */
import { DateTime } from 'luxon';

export type AnnualPlannerUnavailableKey =
  | 'unableToDisplayTracksOrBlocks'
  | 'unableToDisplayBlocks'
  | 'unableToDisplayTracks';

export function annualPlannerUnavailableKey(
  canViewTracks: boolean,
  canViewBlocks: boolean,
): AnnualPlannerUnavailableKey | null {
  if (canViewTracks && canViewBlocks) return null;
  if (!canViewTracks && !canViewBlocks) {
    return 'unableToDisplayTracksOrBlocks';
  }
  return canViewTracks ? 'unableToDisplayBlocks' : 'unableToDisplayTracks';
}

/**
 * Why a block's start date cannot be changed. Blocks with logged (in-progress)
 * or completed workouts, and bowling blocks, must not move — changing their
 * dates would move training history or a template-seeded schedule. Published
 * blocks can move: the shift path re-publishes them automatically (Phase 02).
 *
 * Single source of moveability gating: the Edit Block Drawer reads it today,
 * and the drag paths (later phases) read the same rules.
 */
export type BlockStartDateLockReason = 'inProgress' | 'bowling';

export function blockStartDateLockReason(block: {
  blockType?: string | null;
  hasWorkoutsInProgress?: boolean | null;
  hasPublishedWorkouts?: boolean | null;
}): BlockStartDateLockReason | null {
  if (block.hasWorkoutsInProgress) return 'inProgress';
  if (block.blockType === 'bowling') return 'bowling';
  return null;
}

/**
 * Whether a block can be dragged to a new start date on the Timeline, and why
 * not when it cannot. Reads the same rules as the Edit Block Drawer's
 * start-date lock (`blockStartDateLockReason`) plus the Blocks Edit
 * permission — the drag path extends the drawer's gating, never forks it.
 * Permission wins over moveability: a coach who cannot edit sees the
 * permission reason.
 */
export type BlockDragGateReason = BlockStartDateLockReason | 'permission';

export function blockDragGate(
  block: Parameters<typeof blockStartDateLockReason>[0],
  canEditBlocks: boolean,
): { movable: boolean; reason: BlockDragGateReason | null } {
  if (!canEditBlocks) return { movable: false, reason: 'permission' };
  const lock = blockStartDateLockReason(block);
  return lock
    ? { movable: false, reason: lock }
    : { movable: true, reason: null };
}

/**
 * Why a proposed start-date shift is not allowed, as the i18n key of the
 * message, or null when the shift is allowed. A start-date change may never
 * move the schedule into the past:
 *
 * - A block that starts today or later may move to any date from today on.
 *   Backward moves within the future are fine; entering the past is not.
 * - A block that already starts in the past may only move forward. The new
 *   start date may still be in the past, but it must be later than the
 *   current one.
 *
 * Mirrors the API rule (assertStartDateShiftTargetAllowed). Shared by the
 * Edit Block Drawer's validation and the drag confirm modal so both paths
 * enforce one rule with one message.
 */
export function startDateShiftErrorKey(
  currentStartDate: string,
  newStartDate: string,
  today: string,
): 'startDateShiftForwardOnly' | 'startDateShiftNotInPast' | null {
  const current = DateTime.fromISO(currentStartDate);
  const newStart = DateTime.fromISO(newStartDate);
  const todayDate = DateTime.fromISO(today);
  if (!current?.isValid || !newStart?.isValid || !todayDate?.isValid) {
    return null;
  }

  if (current < todayDate && newStart < current) {
    return 'startDateShiftForwardOnly';
  }
  if (current >= todayDate && newStart < todayDate) {
    return 'startDateShiftNotInPast';
  }
  return null;
}
